/**
 * Employee Roster Page
 *
 * Standalone page for managing employee profiles with server-side
 * fuzzy search, category filtering, sorting, and virtualized rendering
 * for smooth scrolling with 100+ employees.
 */

import { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useProfileListPaginated,
  useDeleteProfile,
} from '@/hooks/nib/useEmployeeProfiles';
import { useRosterSearch } from '@/hooks/useRosterSearch';
import { RosterSearchBar } from '@/components/roster/RosterSearchBar';
import {
  VirtualizedProfileList,
  VirtualizedProfileListRef,
} from '@/components/roster/VirtualizedProfileList';
import { ScrollToTopButton } from '@/components/roster/ScrollToTopButton';
import { EmployeeProfile } from '@/types/nib/employeeProfile';

export default function EmployeeRoster() {
  const {
    search,
    setSearch,
    debouncedSearch,
    filters,
    updateFilters,
    inputRef,
    clearAll,
    hasActiveFilters,
  } = useRosterSearch();

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const listRef = useRef<VirtualizedProfileListRef>(null);

  const {
    data: profileData,
    isLoading,
    isError,
    error,
  } = useProfileListPaginated({
    search: debouncedSearch || undefined,
    category: filters.category,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    activeOnly: filters.activeOnly,
    perPage: 200,
  });

  const profiles = profileData?.profiles ?? [];
  const total = profileData?.total ?? 0;

  const deleteProfile = useDeleteProfile();

  const handleDelete = useCallback(
    async (profile: EmployeeProfile) => {
      if (
        !confirm(
          `Delete profile for ${profile.first_name} ${profile.last_name}?`
        )
      )
        return;
      setDeletingId(profile.id);
      try {
        await deleteProfile.mutateAsync(String(profile.id));
        toast.success('Profile deleted');
      } catch {
        toast.error('Failed to delete profile');
      } finally {
        setDeletingId(null);
      }
    },
    [deleteProfile]
  );

  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      onScroll={(e) => {
        const el = e.currentTarget;
        setShowScrollTop(el.scrollTop > 300);
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Employee Roster
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage saved employee profiles for quick NIB submissions
          </p>
        </div>
        <Link
          to="/nib-c10/manual-entry"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          New Submission
        </Link>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <RosterSearchBar
          search={search}
          onSearchChange={setSearch}
          filters={filters}
          onFiltersChange={updateFilters}
          onClear={clearAll}
          inputRef={inputRef}
          isLoading={isLoading}
          isError={isError}
          errorMessage={error instanceof Error ? error.message : undefined}
          resultCount={total}
          hasActiveFilters={hasActiveFilters}
        />
      </div>

      {/* Virtualized Profile List */}
      <VirtualizedProfileList
        ref={listRef}
        profiles={profiles}
        search={debouncedSearch}
        isLoading={isLoading}
        deletingId={deletingId}
        onDelete={handleDelete}
      />

      {/* Scroll to top */}
      <ScrollToTopButton
        visible={showScrollTop}
        onClick={() => listRef.current?.scrollToTop()}
      />
    </div>
  );
}
